import React, { useState } from 'react';
import Preorder from '../../../models/Preorder';
import PreorderDetail from '../../../models/PreorderDetail';
import Pagination from '../../../models/Pagination';
import { Table, TableBody, TableCell, TableHead, TableRow, CircularProgress, TableContainer, Stack, Tooltip, IconButton } from '@mui/material';
import PaginationButtons from '../../PaginationButtons';
import { formattedCurrency } from '../../../utils/StringUtils';
import moment from 'moment';
import { ISTableTitle, ISTableHeaderCell, ISTableRow } from '../../Utils/ISTable';
import { useTranslation } from 'react-i18next';
import { statusPreorderNameTranslated } from '../../Utils/CommonConst';
import { downloadPreorderFile } from '../../../actions/preorder/preorderDownloadFile';
import { useDispatch } from 'react-redux';
import { AttachFile } from '@mui/icons-material';

export interface Row {
  data: Preorder
  action?: 'create' | 'update' | 'error';
}

interface Props {
  rows: Row[];
  loading: boolean;
  pagination?: Pagination;
  onChangePage?: (page: number) => void;
  onClickInfo?: (preorder: Preorder) => void;
}


interface DetailProps {
  rows: PreorderDetail[];
  loading: boolean;
  pagination?: Pagination;
  onChangePage?: (page: number) => void;
  onClickInfo?: (preorderDetail: PreorderDetail) => void;
}

const AdminPreorders: React.FC<Props> = (props) => {
  const { t } = useTranslation('preorder')
  const [downloading, setDownloading] = useState<string>('')
  const dispatch = useDispatch()

  const mapActionToColor = (action?: string) => {
    switch (action) {
      case 'create': return 'green';
      case 'update': return 'yellow';
      case 'error': return 'red';
      default: return 'default';
    }
  }

  const downloadFilePreorder = async (preorder: Preorder, fileId: string, fileName: string) => {
    if (preorder.attachedFileOnHold && preorder.id) {
      try {
        setDownloading(`${preorder.id}${fileId}`)
        await dispatch(downloadPreorderFile(preorder.id, fileId, 
          (file: any) => {

            const url = URL.createObjectURL(new Blob([file], { type: 'application/pdf' }))
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
              'download',
              fileName ? fileName : `Retencion-Carrito-${preorder.id}.pdf`
            );
            document.body.appendChild(link);
            // Start download
            link.click();

            URL.revokeObjectURL(url);
          }))
        setDownloading('')
      }
      catch {
      }
    }
  }


  return (
    <Stack style={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - var(--header-height)', borderRadius: "10px", margin: "10px", minWidth: "770px" }}>
      <ISTableTitle sx={{ display: "flex", flexDirection: "row", alignItems: 'center', justifyContent: 'space-between' }} title={t('preorderTitle')} />
      <TableContainer sx={{ overflowX: 'auto', height: '100%' }}>
        <Table size="medium" stickyHeader={true}>
          <TableHead >
            <TableRow selected={true}>
              <ISTableHeaderCell align='center'>{t('date')}</ISTableHeaderCell>
              <ISTableHeaderCell align='center'>{t('number')}</ISTableHeaderCell>
              <ISTableHeaderCell align='center'>{t('status')}</ISTableHeaderCell>
              <ISTableHeaderCell align='center'></ISTableHeaderCell>
              <ISTableHeaderCell>{t('client')}</ISTableHeaderCell>
              <ISTableHeaderCell>{t('clientName')}</ISTableHeaderCell>
              {!props.loading && props.rows && props.rows.length > 0 && ['C','X','E'].some( char => props.rows[0].data.status?.includes(char) ) && <ISTableHeaderCell align='center'>{t('estimatedDeliveryDate')}</ISTableHeaderCell>}
              <ISTableHeaderCell>{t("madeBy")}</ISTableHeaderCell>
              <ISTableHeaderCell align='center'>{t('prepaymentId')}</ISTableHeaderCell>
              <ISTableHeaderCell align='right'>{t('cumulative')}</ISTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!props.loading && props.rows.map((row, index) =>
              <ISTableRow key={index} color={mapActionToColor(row.action)} sx={{ cursor: 'pointer' }}
                onClick={(e) => { e.stopPropagation(); props.onClickInfo && props.onClickInfo(row.data) }}>
                <TableCell align='center' sx={{ borderSpacing: "18px 12px" }}>{moment(row.data.dateUpdated ? row.data.dateUpdated : row.data.dateCreated).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY')}</TableCell>
                <TableCell align='center'>{row.data.id}</TableCell>
                <TableCell align='center' /*onClick={(e) => { e.stopPropagation(); setCurrentPreorder(row.data); setShowCloseDialog(true) }}*/>
                  {statusPreorderNameTranslated(row.data.status!, t)}
                </TableCell>
                <TableCell >
                    {row.data.attachedFileOnHold ?
                      <Tooltip title={row.data.fileNameHolded ? row.data.fileNameHolded : ''}>
                        <IconButton color="secondary"
                          onClick={(e) => { e.stopPropagation(); row.data.attachedFileOnHold && row.data.fileNameHolded && downloadFilePreorder(row.data, row.data.attachedFileOnHold, row.data.fileNameHolded) }}>
                          {downloading === `${row.data.id}${row.data.attachedFileOnHold}` ? <CircularProgress size={15} /> : <AttachFile fontSize="small" />}
                        </IconButton>
                      </Tooltip> : ''}

                    {row.data.attachedFileOnReject ?
                      <Tooltip title={row.data.fileNameRejected ? row.data.fileNameRejected : ''}>
                        <IconButton color="secondary"
                          onClick={(e) => { e.stopPropagation(); row.data.attachedFileOnReject && row.data.fileNameRejected && downloadFilePreorder(row.data, row.data.attachedFileOnReject, row.data.fileNameRejected) }}>
                          {downloading === `${row.data.id}${row.data.attachedFileOnReject}` ? <CircularProgress size={15} /> : <AttachFile fontSize="small" />}
                        </IconButton>
                      </Tooltip> : ''}
                  </TableCell>

                <TableCell>{row.data.clientId}</TableCell>
                <TableCell>{row.data.clientName ?? ''}</TableCell>
                {!props.loading && props.rows && props.rows.length > 0 && ['C','X','E'].some( char => props.rows[0].data.status?.includes(char) ) && <TableCell align='center'>{row.data.estimatedDeliveryDate || row.data.manualEstimatedDeliveryDate ? `${moment(row.data.manualEstimatedDeliveryDate ? row.data.manualEstimatedDeliveryDate : row.data.estimatedDeliveryDate).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY')} ${row.data.manualEstimatedDeliveryDate ? '(M)' : `\u00A0\u00A0\u00A0`}`: ''}</TableCell>}
                <TableCell>{row.data.owner?.fullName ?? ''}</TableCell>
                <TableCell align='center'>{row.data.prepaymentId}</TableCell>
                <TableCell align='right'>{row.data.detail && formattedCurrency(row.data.detail.reduce((amount, det) => amount + Number(det.amount), 0))}</TableCell>
              </ISTableRow>
            )}
          </TableBody>
        </Table>
        
        {props.loading &&
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={25} />
          </div>
        }
        {<PaginationButtons pagination={props.pagination} />}
      </TableContainer>
    </Stack>
  );
}

export default AdminPreorders

export const AdminPreorderDetails: React.FC<DetailProps> = (props) => {
  const { t } = useTranslation('preorder')
  return (
    <Stack style={{ display: 'flex', flexDirection: 'column', height: '80%', borderRadius: "10px", margin: "10px", minWidth: "500px" }}>
      <ISTableTitle title={t('titleDetail')} />
      <TableContainer sx={{ overflowX: 'auto', height: '100%' }}>
        <Table size="medium" stickyHeader >
          <TableHead>
            <TableRow selected={true}>
              <ISTableHeaderCell>{t('brand')}</ISTableHeaderCell>
              <ISTableHeaderCell>{t('description')}</ISTableHeaderCell>
              <ISTableHeaderCell align='center'>{t('code')}</ISTableHeaderCell>
              <ISTableHeaderCell align='right'>{t('packageQty')}</ISTableHeaderCell>
              <ISTableHeaderCell align='right'>{t('priceList')}</ISTableHeaderCell>
              <ISTableHeaderCell align='right'>{t('price')}</ISTableHeaderCell>
              <ISTableHeaderCell align='right'>{t('qty')}</ISTableHeaderCell>
              <ISTableHeaderCell align='right'>{t('subtotal')}</ISTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!props.loading &&
              props.rows.map((row, index) => {
                return (
                  <ISTableRow color='default' key={index}>
                    <TableCell>{row.product?.brand?.description}</TableCell>
                    <TableCell>{row.productName}</TableCell>
                    <TableCell align='center'>{row.code}</TableCell>
                    <TableCell align='right'>{Number(row.package as unknown).toFixed(0)}</TableCell>
                    <TableCell align='right'>{formattedCurrency(row.listPrice)}</TableCell>
                    <TableCell align='right'>{formattedCurrency(row.price)}</TableCell>
                    <TableCell align='right'>{Number(row.qty as unknown).toFixed(0)}</TableCell>
                    <TableCell align='right'>{formattedCurrency(row.amount)}</TableCell>
                  </ISTableRow>
                )
              })
            }
          </TableBody>
        </Table>
        {props.loading &&
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={25} />
          </div>
        }
        {<PaginationButtons pagination={props.pagination} />}
      </TableContainer>
    </Stack>
  );
}

